import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useSession, signOut } from '@/lib/auth';
import { Button } from '@/components/ui/Button';
import { useCart } from '@/store/cart';
import { useSidebar } from '@/store/sidebar';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

import styles from './Sidebar.module.css';

/**
 * Sidebar — navegación principal de Mi Boleta como barra lateral (reemplaza al
 * header). Referencia: Sprint 4 Wallet Experience (sidebar oscuro + item activo).
 *
 * Incluye: secciones anidadas (Eventos → Categorías/Ciudades; Mis Tickets →
 * Wallet/Historial), wordmark blanco legible, y colapsar/expandir (preferencia
 * persistida). Responsive: sidebar fija en desktop (lg+), top bar + drawer en
 * mobile. `AppShell` desplaza el contenido según el estado colapsado.
 */

type IconProps = { className?: string };
const svg = (children: JSX.Element) =>
  function Icon({ className }: IconProps): JSX.Element {
    return (
      <svg
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
      >
        {children}
      </svg>
    );
  };

const HomeIcon = svg(
  <>
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </>,
);
const CalendarIcon = svg(
  <>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </>,
);
// StoreIcon — usado sólo por el ítem "Tienda", oculto de la navegación (ver abajo).
// const StoreIcon = svg(<><path d="M3 9l1.5-5h15L21 9M4 9v11h16V9M9 20v-6h6v6" /></>);
const CrownIcon = svg(
  <>
    <path d="M3 7l4 5 5-7 5 7 4-5v11H3z" />
  </>,
);
const SparklesIcon = svg(
  <>
    <path d="M12 3l1.9 4.6L18.5 9l-4.6 1.9L12 15.5l-1.9-4.6L5.5 9l4.6-1.4z" />
  </>,
);
const BoltIcon = svg(
  <>
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </>,
);
const CartIcon = svg(
  <>
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
  </>,
);
const TicketIcon = svg(
  <>
    <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2 2 2 0 0 0 0 4 2 2 0 0 1-2 2H5a2 2 0 0 1-2-2 2 2 0 0 0 0-4z" />
    <path d="M13 5v14" />
  </>,
);
const GridIcon = svg(
  <>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
  </>,
);
const MapPinIcon = svg(
  <>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </>,
);
const WalletIcon = svg(
  <>
    <rect x="3" y="6" width="18" height="13" rx="2" />
    <path d="M3 10h18" />
    <circle cx="16.5" cy="14" r="1" />
  </>,
);
const ClockIcon = svg(
  <>
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </>,
);
const ChevronDownIcon = svg(<polyline points="6 9 12 15 18 9" />);
const ChevronsLeftIcon = svg(
  <>
    <polyline points="11 17 6 12 11 7" />
    <polyline points="18 17 13 12 18 7" />
  </>,
);

type Leaf = { to: string; label: string; Icon: (p: IconProps) => JSX.Element };
type Entry =
  | {
      kind: 'link';
      to: string;
      end?: boolean;
      label: string;
      Icon: (p: IconProps) => JSX.Element;
      cart?: boolean;
    }
  | {
      kind: 'section';
      key: string;
      label: string;
      Icon: (p: IconProps) => JSX.Element;
      parentTo: string;
      children: Leaf[];
    };

export function Sidebar(): JSX.Element {
  const collapsed = useSidebar((s) => s.collapsed);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* Desktop: sidebar fija. El ancho va por inline style (confiable en dev y
          prod; evita el purgado del JIT de Tailwind sobre utilidades responsive
          dinámicas). Es `hidden` en mobile, así que el width solo cuenta en lg+. */}
      <aside
        style={{ width: collapsed ? 64 : 256 }}
        className="hidden flex-col border-r border-white/10 bg-[#0A1B3D] transition-[width] duration-200 lg:fixed lg:inset-y-0 lg:left-0 lg:z-30 lg:flex"
      >
        <MenuContent collapsed={collapsed} showToggle />
      </aside>

      {/* Mobile: top bar con hamburguesa */}
      <div className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-white/10 bg-[#0A1B3D] px-4 lg:hidden">
        <button
          type="button"
          aria-label="Abrir menú"
          onClick={() => setDrawerOpen(true)}
          className="inline-flex h-10 w-10 items-center justify-center rounded-card text-white/90 hover:bg-white/10"
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <Brand />
        <span className="h-10 w-10" />
      </div>

      {/* Mobile: drawer overlay */}
      {drawerOpen && (
        <div className="fixed inset-0 z-40 flex lg:hidden">
          <button
            type="button"
            aria-label="Cerrar menú"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-[#070B18]/60"
          />
          <aside className="relative z-10 flex h-full w-72 max-w-[82%] flex-col bg-[#0A1B3D] shadow-xl">
            <button
              type="button"
              aria-label="Cerrar menú"
              onClick={() => setDrawerOpen(false)}
              className="absolute right-3 top-3 z-10 inline-flex h-9 w-9 items-center justify-center rounded-card text-white/70 hover:bg-white/10"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <MenuContent
              collapsed={false}
              showToggle={false}
              onNavigate={() => setDrawerOpen(false)}
            />
          </aside>
        </div>
      )}
    </>
  );
}

/**
 * Marca — lockup oficial de Mi Boleta sobre fondo oscuro (referencia del
 * Operator): símbolo azul (`/mi-boleta-mark.png`, asset oficial que contrasta
 * sobre el navy) + wordmark "Mi Boleta" en blanco + tagline. El png completo
 * `mi-boleta-logo.png` tiene el wordmark en azul oscuro (para fondo claro) y no
 * es legible acá; por eso se usa el MARK oficial + el wordmark en blanco.
 */
function Brand({
  collapsed = false,
  onNavigate,
}: {
  collapsed?: boolean;
  onNavigate?: () => void;
}): JSX.Element {
  return (
    <Link
      to="/"
      aria-label="Mi Boleta — Inicio"
      onClick={onNavigate}
      className={`inline-flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}
    >
      <img
        src="/mi-boleta-mark.png"
        alt="Mi Boleta"
        className="flex-none object-contain"
        style={{ height: collapsed ? 34 : 44, width: 'auto' }}
      />
      {!collapsed && (
        <span className="flex flex-col leading-none">
          <span
            className="text-[26px] font-extrabold leading-none tracking-tight"
            style={{ color: '#FFFFFF' }}
          >
            Mi Boleta
          </span>
          <span
            className="mt-1.5 text-[9px] font-semibold uppercase leading-none"
            style={{ color: 'rgba(255,255,255,0.55)', letterSpacing: '0.24em' }}
          >
            Experiencias a otro nivel
          </span>
        </span>
      )}
    </Link>
  );
}

function MenuContent({
  collapsed,
  showToggle,
  onNavigate,
}: {
  collapsed: boolean;
  showToggle: boolean;
  onNavigate?: () => void;
}): JSX.Element {
  const { t } = useTranslation('common');
  const toggle = useSidebar((s) => s.toggle);
  const { pathname } = useLocation();
  const [open, setOpen] = useState<Record<string, boolean>>({ eventos: true, 'mis-tickets': true });

  const cartCount = useCart(
    (s) =>
      s.lines.reduce((sum, l) => sum + l.quantity, 0) +
      s.productLines.reduce((sum, l) => sum + l.quantity, 0),
  );

  const ENTRIES: Entry[] = [
    { kind: 'link', to: '/', end: true, label: t('nav.home'), Icon: HomeIcon },
    {
      kind: 'section',
      key: 'eventos',
      label: t('nav.events'),
      Icon: CalendarIcon,
      parentTo: '/eventos',
      children: [
        { to: '/eventos/categorias', label: 'Categorías', Icon: GridIcon },
        { to: '/eventos/ciudades', label: 'Ciudades', Icon: MapPinIcon },
      ],
    },
    // Tienda oculta de la navegación (pedido del Operator 2026-08-21). La página
    // y las rutas /store se conservan; sólo se oculta su punto de acceso en la UI.
    // { kind: 'link', to: '/store', label: t('nav.shop'), Icon: StoreIcon },
    { kind: 'link', to: '/memberships', label: t('nav.memberships'), Icon: CrownIcon },
    { kind: 'link', to: '/experiences', label: t('nav.experiences'), Icon: SparklesIcon },
    { kind: 'link', to: '/flash-sales', label: t('nav.flashSales'), Icon: BoltIcon },
    {
      kind: 'section',
      key: 'mis-tickets',
      label: 'Mis Tickets',
      Icon: TicketIcon,
      parentTo: '/my-purchases',
      children: [
        { to: '/my-purchases', label: 'Wallet', Icon: WalletIcon },
        { to: '/mis-tickets/historial', label: 'Historial', Icon: ClockIcon },
      ],
    },
    { kind: 'link', to: '/cart', label: t('nav.cart'), Icon: CartIcon, cart: true },
  ];

  const linkClass = (isActive: boolean) =>
    `flex items-center gap-3 rounded-card ${collapsed ? 'justify-center px-0 py-2.5' : 'px-3 py-2.5'} text-sm transition-colors ${
      isActive
        ? 'bg-primary font-semibold text-white'
        : 'text-white/70 hover:bg-white/10 hover:text-white'
    }`;

  return (
    <div className="flex h-full flex-col">
      {/* Marca + toggle */}
      <div
        className={`flex items-center px-4 py-4 ${collapsed ? 'flex-col gap-3' : 'justify-between'}`}
      >
        <Brand collapsed={collapsed} onNavigate={onNavigate} />
        {showToggle && (
          <button
            type="button"
            onClick={toggle}
            title={collapsed ? 'Expandir menú' : 'Contraer menú'}
            aria-label={collapsed ? 'Expandir menú' : 'Contraer menú'}
            className="hidden h-8 w-8 items-center justify-center rounded-card text-white/60 hover:bg-white/10 hover:text-white lg:inline-flex"
          >
            <ChevronsLeftIcon className={collapsed ? 'rotate-180' : ''} />
          </button>
        )}
      </div>

      {/* Nav principal */}
      <nav
        aria-label="Primary"
        className={`flex-1 space-y-1 overflow-y-auto px-3 ${styles.noScrollbar}`}
      >
        {ENTRIES.map((entry) => {
          if (entry.kind === 'link') {
            return (
              <NavLink
                key={entry.to}
                to={entry.to}
                end={entry.end}
                onClick={onNavigate}
                title={collapsed ? entry.label : undefined}
                className={({ isActive }) => linkClass(isActive)}
                aria-label={
                  entry.cart && cartCount > 0
                    ? t('nav.cartWithCount', { count: cartCount })
                    : undefined
                }
              >
                <span className="relative flex-none">
                  <entry.Icon />
                  {entry.cart && cartCount > 0 && (
                    <span className="absolute -right-2 -top-2 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-[#0A1B3D]">
                      {cartCount > 99 ? '99+' : cartCount}
                    </span>
                  )}
                </span>
                {!collapsed && <span>{entry.label}</span>}
              </NavLink>
            );
          }

          // Sección anidada
          const isOpen = open[entry.key];
          const sectionActive = [entry.parentTo, ...entry.children.map((c) => c.to)].some(
            (p) => pathname === p,
          );

          if (collapsed) {
            // Rail: solo el ícono de la sección → navega al padre.
            return (
              <Link
                key={entry.key}
                to={entry.parentTo}
                onClick={onNavigate}
                title={entry.label}
                className={linkClass(sectionActive)}
              >
                <entry.Icon className="flex-none" />
              </Link>
            );
          }

          return (
            <div key={entry.key}>
              <div
                className={`flex items-center rounded-card ${sectionActive ? 'text-white' : 'text-white/70'}`}
              >
                <Link
                  to={entry.parentTo}
                  onClick={() => {
                    onNavigate?.();
                    setOpen((o) => ({ ...o, [entry.key]: true }));
                  }}
                  className="flex flex-1 items-center gap-3 rounded-card px-3 py-2.5 text-sm transition-colors hover:bg-white/10 hover:text-white"
                >
                  <entry.Icon className="flex-none" />
                  <span>{entry.label}</span>
                </Link>
                <button
                  type="button"
                  onClick={() => setOpen((o) => ({ ...o, [entry.key]: !o[entry.key] }))}
                  aria-label={isOpen ? 'Contraer' : 'Expandir'}
                  aria-expanded={isOpen}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-card text-white/50 hover:bg-white/10 hover:text-white"
                >
                  <ChevronDownIcon
                    className={`h-4 w-4 transition-transform ${isOpen ? '' : '-rotate-90'}`}
                  />
                </button>
              </div>
              {isOpen && (
                <div className="mt-1 space-y-1 pl-5">
                  {entry.children.map((leaf) => (
                    <NavLink
                      key={leaf.to}
                      to={leaf.to}
                      end
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-card px-3 py-2 text-sm transition-colors ${isActive ? 'bg-primary font-semibold text-white' : 'text-white/60 hover:bg-white/10 hover:text-white'}`
                      }
                    >
                      <leaf.Icon className="h-[18px] w-[18px] flex-none" />
                      <span>{leaf.label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Footer: idioma + cuenta */}
      <div className="mt-auto space-y-2 border-t border-white/10 px-3 py-4">
        {!collapsed && (
          <div className="px-1">
            <LanguageSwitcher />
          </div>
        )}
        <AccountBlock collapsed={collapsed} onNavigate={onNavigate} />
      </div>
    </div>
  );
}

function AccountBlock({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}): JSX.Element {
  const { t } = useTranslation('common');
  const { user, loading } = useSession();

  const UserIcon = svg(
    <>
      <circle cx="12" cy="7" r="4" />
      <path d="M5.5 21a6.5 6.5 0 0 1 13 0" />
    </>,
  );

  if (!user) {
    if (collapsed) {
      return (
        <Link
          to="/login"
          onClick={onNavigate}
          title={t('nav.login')}
          className="flex items-center justify-center rounded-card py-2.5 text-white/80 hover:bg-white/10 hover:text-white"
        >
          <UserIcon />
        </Link>
      );
    }
    return (
      // "Crear cuenta" va DEBAJO de "Ingresar" para que quien todavía no tiene
      // cuenta vea la salida de inmediato, sin entrar primero a la pantalla de
      // ingreso a buscarla.
      <div className="flex w-full flex-col gap-2">
        <Button
          as={Link}
          to="/login"
          variant="primary"
          size="sm"
          disabled={loading}
          className="w-full"
        >
          {t('nav.login')}
        </Button>
        {/*
          Se estiliza a mano en vez de usar una variante de `Button`: las cuatro
          están pensadas para fondo claro (`ghost` es `text-neutral-700`) y sobre
          el azul de la barra quedarían ilegibles. Estas clases son las mismas
          que ya usan los ítems de esta barra, así que el botón pertenece.

          Apunta a `/login` con el modo en la URL, NO a la ruta `/signup`: esa es
          otra pantalla, más vieja, que pide documento y nombre de usuario en vez
          de los campos de este formulario. Mandar ahí desde acá le mostraría al
          fan un registro distinto del que se aprobó.
        */}
        <Link
          to="/login?modo=registro"
          onClick={onNavigate}
          className="flex w-full items-center justify-center rounded-card border border-white/20 px-3 py-1.5 text-sm font-medium text-white/90 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/30"
        >
          {t('nav.signup')}
        </Link>
      </div>
    );
  }

  if (collapsed) {
    return (
      <Link
        to="/account"
        onClick={onNavigate}
        title={t('nav.myAccount')}
        className="flex items-center justify-center rounded-card py-2.5 text-white/80 hover:bg-white/10 hover:text-white"
      >
        <UserIcon />
      </Link>
    );
  }

  const linkClass = (isActive: boolean) =>
    `block rounded-card px-3 py-2 text-sm transition-colors ${isActive ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'}`;

  return (
    <div className="space-y-1">
      {/* "Mis compras" se retiró de acá: era un punto de entrada DUPLICADO a
          /my-purchases (ya cubierto por el acordeón "Mis Tickets" → Wallet /
          Historial), y encendía su estado activo a la par del acordeón. */}
      <NavLink
        to="/account"
        end
        onClick={onNavigate}
        className={({ isActive }) => linkClass(isActive)}
      >
        {t('nav.myAccount')}
      </NavLink>
      <button
        type="button"
        onClick={() => {
          onNavigate?.();
          void signOut();
        }}
        className="block w-full rounded-card px-3 py-2 text-left text-sm text-white/70 transition-colors hover:bg-white/10 hover:text-white"
      >
        {t('nav.logout')}
      </button>
    </div>
  );
}
